// @ts-ignore
import {
  Box,
  CircularProgress,
  Typography,
  Popover,
  Divider,
  MenuItem,
  MenuList,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { FileField } from "models/interfaces";
import {
  DragPreview,
  getDocumentDimensions,
  getNextAvailableId,
  PDF_VIEWER_PAGE_SELECTOR,
} from "../SignRequestHelper";
import { useAppSelector } from "hooks/use-app-select";
import { RootState } from "utils/store";
import DraggableField from "./DraggableField";
import { UseDocument } from "./UseDocument";
import {
  redoFieldChange,
  setReduxSelectedFile,
  selectField,
  setSelectedField,
  setSelectedFieldDrawer,
  setSideCollpased,
  undoFieldChange,
  updateFileInSignRequest,
} from "redux/appSlice";
import { getDocumentUrl } from "../SignRequestService";
import { useAppDispatch } from "redux/hooks";
import strings from "global/constants/StringConstants";
import { convertFileForBackend } from "screens/Shared/CommonHelper";
import { usePopover } from "hooks/use-popover";
import ContextMenu from "./ContextMenu";
import { isTruthy } from "helpers/methods";
import { hasAccessTo } from "layouts/Config";
import editorTokens from "../SignRequestEditor.tokens";
import { tourAttr } from "tour";
import { visuallyHidden } from "@mui/utils";
import { PLACED_FIELD_HELP_ID } from "../SignRequestHelper";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const DocumentViewer = ({
  isFieldWithinBounds,
  previewCoords,
  selectedPreviewField,
  setSelectedPreviewField,
  docLoading,
  setDocLoading,
  isMobile,
  onFieldSelect,
  onPasteAnchor,
  pasteFieldAt,
  canPaste,
  zoom = 1,
  topBanner,
  onPageInfoChange,
  goToPageRef,
}: {
  isFieldWithinBounds: boolean;
  previewCoords: { x: number; y: number };
  selectedPreviewField: string | null | undefined;
  setSelectedPreviewField: (value: string | null) => void;
  docLoading: boolean;
  setDocLoading: (value: boolean) => void;
  isMobile?: boolean;
  onFieldSelect?: (field: FileField | null) => void;
  onPasteAnchor?: (
    anchor: { page: number; fieldPageX: number; fieldPageY: number } | null,
  ) => void;
  pasteFieldAt?: (page: number, fieldPageX: number, fieldPageY: number) => void;
  canPaste?: boolean;
  zoom?: number;
  topBanner?: React.ReactNode;
  /** Lets the editor toolbar mirror the viewer's "Page x of n" state. */
  onPageInfoChange?: (info: { current: number; total: number }) => void;
  /**
   * Filled with the viewer's jump-to-page handler. The floating page pill lives
   * outside this scroll container (it must not scroll with the pages), so the
   * editor drives paging through this handle.
   */
  goToPageRef?: React.MutableRefObject<((page: number) => void) | null>;
}) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  // Mobile field selection is detected here (container, capture phase) because
  // react-rnd swallows click/mouse events on the field element itself.
  const fieldPointerStartRef = useRef<{
    x: number;
    y: number;
    id: number;
  } | null>(null);
  const [width, setWidth] = useState<number>(0);
  const { selectedField } = useAppSelector((state: RootState) => state.app);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pagesHeight, setPagesHeight] = useState<number | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [visiblePages, setVisiblePages] = useState<number[]>([1]);
  const [docLoadSuccess, setDocLoadSuccess] = useState(false);
  const [assignFieldsPopup, setAssignFieldsPopup] = useState<boolean>(false);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [url, setUrl] = useState("");
  const [currentField, setCurrentField] = useState<any>(selectedField);
  const editSignatureCallbacks = useRef<Record<string, () => void>>({});
  const { isBulkSend, sideCollpased } = useAppSelector((state) => state.app);
  const popover = usePopover<any>();

  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const longPressFiredRef = useRef(false);
  const LONG_PRESS_DELAY = 500;
  const LONG_PRESS_MOVE_THRESHOLD = 10;

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    longPressStartRef.current = null;
  };

  const [pastePopover, setPastePopover] = useState<{
    mouseX: number;
    mouseY: number;
    page: number;
    fieldPageX: number;
    fieldPageY: number;
  } | null>(null);

  const computePagePosition = (
    pageElement: HTMLElement,
    clientX: number,
    clientY: number,
  ): { fieldPageX: number; fieldPageY: number } => {
    const rect = pageElement.getBoundingClientRect();
    const fieldPageX = ((clientX - rect.left) / rect.width) * 100;
    const fieldPageY = ((clientY - rect.top) / rect.height) * 100;
    return {
      fieldPageX: Math.min(Math.max(fieldPageX, 0), 100),
      fieldPageY: Math.min(Math.max(fieldPageY, 0), 100),
    };
  };

  const closePastePopover = () => setPastePopover(null);

  // Fraction of the canvas width the page renders at on desktop. Raised from
  // 0.88 so the document fills more width and leaves less empty space on the
  // left/right. Fields are stored as percentages of the rendered page, so they
  // scale with it — widening the page does not move any placed field.
  const DESKTOP_PAGE_INSET = 0.97;
  const basePageWidth = width - (isMobile ? 0 : 10);
  const renderedPageWidth = isMobile
    ? basePageWidth
    : Math.max(240, Math.round(basePageWidth * DESKTOP_PAGE_INSET * zoom));

  const isSignAccess = hasAccessTo(
    strings.resources.sign,
    strings.fetchPermission,
  );

  const isTemplateAccess = hasAccessTo(
    strings.resources.template,
    strings.fetchPermission,
  );

  const {
    signRequest,
    selectedFile,
    isRequest,
    assignExistingFields,
    setAssignExistingFields,
    historyByDoc,
  } = useAppSelector((state: RootState) => state.app);

  const docContextDocId =
    selectedFile?.unsignedDoc || selectedFile?.unsignedPdf || "";
  const docContextHistory = historyByDoc?.[docContextDocId];
  const canUndoDoc = (docContextHistory?.past?.length || 0) > 0;
  const canRedoDoc = (docContextHistory?.future?.length || 0) > 0;

  const { isWithinPageBounds, getFieldPosition } = UseDocument();

  // Load URL when file changes.
  useEffect(() => {
    const newUrl = selectedFile?.unsignedDocUrl || selectedFile?.unsignedPdfUrl;
    const fileId = selectedFile?.unsignedDoc || selectedFile?.unsignedPdf;
    if (newUrl) {
      setDocLoadSuccess(false);
      setVisiblePages([1]);
      setNumPages(0);
      setIsLoadingPages(false);
      setPagesHeight(null);
      setUrl(newUrl);
    } else if (isTruthy(fileId)) {
      getDocUrl();
    }
  }, [selectedFile?.unsignedPdfUrl, selectedFile?.unsignedDocUrl]);

  // Load page dimensions for the selected file. These are required to convert
  // an existing field's PDF-point coords (x/y/w/h) into the on-screen coords the
  // editor renders and drags with — without them the field gets zero-size coords
  // and can't be grabbed. The envelope id can arrive AFTER the first document
  // mounts (and the fetch fails without it), and the URL effect above only
  // re-runs on URL change, so the first document would otherwise never retry.
  // Keying this effect on the id + file + dimensions makes it retry as soon as
  // the id lands. The `!isTruthy(dimensions)` guard stops it re-fetching once the
  // dimensions are in.
  useEffect(() => {
    const fileId = selectedFile?.unsignedDoc || selectedFile?.unsignedPdf;
    if (!isTruthy(fileId) || isTruthy(selectedFile?.dimensions)) return;
    if ((isRequest && !isSignAccess) || (!isRequest && !isTemplateAccess)) {
      return;
    }
    if (!isTruthy(signRequest?.id)) return;
    getDocumentDimensions(selectedFile, isRequest, signRequest, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFile?.unsignedDoc,
    selectedFile?.unsignedPdf,
    selectedFile?.dimensions,
    signRequest?.id,
    isRequest,
    isSignAccess,
    isTemplateAccess,
  ]);

  const getDocUrl = async () => {
    if ((isRequest && !isSignAccess) || (!isRequest && !isTemplateAccess)) {
      return;
    }
    try {
      setDocLoading(true);
      const response = await getDocumentUrl(
        convertFileForBackend(selectedFile),
        isRequest,
        signRequest.id!,
        false,
      );
      setUrl(response.message);
      dispatch(
        updateFileInSignRequest({
          ...selectedFile,
          unsignedDocUrl: response.message,
        }),
      );
    } finally {
      // keep docLoading true until PDF actually loads
    }
  };

  useEffect(() => {
    if (pageRef.current) {
      setPagesHeight(pageRef.current.offsetHeight);
    }
  }, [numPages, visiblePages]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        // Mobile renders pages nearly full-width (a "little zoom"); desktop
        // keeps the larger inset.
        const measured = containerRef.current.offsetWidth - (isMobile ? 4 : 20);
        if (measured > 0) {
          setWidth((prev) => (Math.abs(prev - measured) < 1 ? prev : measured));
        }
      }
    };
    handleResize();
    // ResizeObserver re-measures once the container actually has a width.
    // Fixes the mobile flex layout where offsetWidth can be 0 at mount, which
    // otherwise left `width` at 0 and showed the loader forever.
    let observer: ResizeObserver | undefined;
    let frame = 0;
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      observer = new ResizeObserver(() => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(handleResize);
      });
      observer.observe(containerRef.current);
    }
    window.addEventListener("resize", handleResize);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * The page whose top edge is closest to the top of the scroll viewport. The
   * design's toolbar caption and the floating page pill both read off this.
   */
  const syncCurrentPage = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerTop = container.getBoundingClientRect().top;
    const pages = Array.from(
      container.querySelectorAll<HTMLElement>(PDF_VIEWER_PAGE_SELECTOR),
    );
    if (!pages.length) return;

    let nearest = 1;
    let nearestDistance = Number.POSITIVE_INFINITY;
    pages.forEach((page) => {
      const number = Number(page.getAttribute("data-page-number") ?? "1");
      const distance = Math.abs(page.getBoundingClientRect().top - containerTop);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = number;
      }
    });
    setCurrentPage(nearest);
  }, []);

  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || (numPages && page > numPages)) return;
      // Pages load five at a time; make sure the target is mounted first.
      setVisiblePages((prev) =>
        prev.includes(page)
          ? prev
          : Array.from({ length: page }, (_, i) => i + 1),
      );
      window.requestAnimationFrame(() => {
        const target = containerRef.current?.querySelector<HTMLElement>(
          `${PDF_VIEWER_PAGE_SELECTOR}[data-page-number="${page}"]`,
        );
        target?.scrollIntoView({ block: "start", behavior: "smooth" });
        setCurrentPage(page);
      });
    },
    [numPages],
  );

  const handleScroll = () => {
    if (!containerRef.current) return;
    syncCurrentPage();
    if (isLoadingPages) return;

    const { scrollTop, clientHeight, scrollHeight } = containerRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setIsLoadingPages(true);

      setTimeout(() => {
        setVisiblePages((prevPages) => {
          const lastLoadedPage = prevPages[prevPages.length - 1];
          const nextPages = Array.from(
            { length: 5 },
            (_, i) => lastLoadedPage + i + 1,
          ).filter((page) => page <= numPages);

          return [...prevPages, ...nextPages];
        });

        setIsLoadingPages(false);
      }, 1000);
    }
  };

  useEffect(() => {
    onPageInfoChange?.({ current: currentPage, total: numPages });
  }, [currentPage, numPages, onPageInfoChange]);

  useEffect(() => {
    if (!goToPageRef) return;
    goToPageRef.current = goToPage;
    return () => {
      goToPageRef.current = null;
    };
  }, [goToPage, goToPageRef]);

  const handleDocumentLoad = (doc: any) => {
    setDocLoading(false);
    setCurrentPage(1);
    if (doc.numPages > 1) {
      setVisiblePages([1, 2]);
    } else if (doc.numPages === 1) {
      setVisiblePages([1]);
    }
    setNumPages(doc.numPages);
    setDocLoadSuccess(true);
    if (assignExistingFields[selectedFile.unsignedPdf]) {
      setAssignFieldsPopup(true);
    }
  };

  // Capture the PDF's own page dimensions (in points) as they render. This is
  // the same data the backend `dimensions` call returns, but sourced directly
  // from the rendered document so it NEVER fails — the backend call can fail or
  // race the envelope id (notably on the first document), which left existing
  // fields with no coordinates and unmovable. We only fill pages that don't
  // already have valid dimensions, so a successful backend fetch still wins.
  const handlePageLoad = (pageNumber: number, page: any) => {
    const pageIndex = pageNumber - 1;
    const width = page?.originalWidth;
    const height = page?.originalHeight;
    if (!width || !height) return;

    const currentDims: any = (selectedFile as any)?.dimensions;
    const existing = currentDims?.[pageIndex];
    if (existing?.x && existing?.y) return;

    const mergedDims = { ...currentDims, [pageIndex]: { x: width, y: height } };
    const updatedFile = { ...selectedFile, dimensions: mergedDims };
    dispatch(setReduxSelectedFile(updatedFile));
    dispatch(updateFileInSignRequest(updatedFile));
  };

  const updateField = (index: number, updatedField: any) => {
    const updatedFields = [...selectedFile.fields];
    updatedFields[index] = updatedField;
    const updatedFile = {
      ...selectedFile,
      fields: updatedFields,
    };
    dispatch(setReduxSelectedFile(updatedFile));
    dispatch(updateFileInSignRequest(updatedFile));
  };

  const onFieldResize = useCallback(
    (node: HTMLElement, index: number, event: any) => {
      const field = selectedFile.fields[index];
      const isText = [
        strings.text,
        strings.email,
        strings.name,
        strings.title,
        strings.companyName,
        strings.dateSigned,
      ].includes(field.type);

      const $page = window.document.querySelector<HTMLElement>(
        `${PDF_VIEWER_PAGE_SELECTOR}[data-page-number="${field.page + 1}"]`,
      );

      if (!$page) return;

      let {
        x: pageX,
        y: pageY,
        width: pageWidth,
        height: pageHeight,
      } = getFieldPosition($page, node);

      if (pageX <= 0) pageX = 0.05;
      if (pageY <= 0) pageY = 0.1;

      if (isText && event.type === "resize") {
        pageHeight =
          (pageHeight * field.height) / 100 < field.styles.size + 8
            ? (field.styles.size * 100) / field.height +
            (8 * 100) / field.height
            : pageHeight;
      } else if (field.type === strings.checkbox && event.type === "resize") {
        pageHeight =
          (pageHeight * field.height) / 100 < 24
            ? (24 * 100) / field.height
            : pageHeight;
      }

      if (pageX >= 100 || pageY >= 100) return;

      const storedPageWidth =
        field.type === strings.checkbox ? pageHeight : pageWidth;
      // Keep the PDF-point coords (x/y/w/h) in sync with the new percentage
      // position. Existing fields are positioned from x/y (calculateExitingCoords);
      // if we only updated the percentages the field would snap back to its stale
      // x/y on the next recompute.
      const dims = (selectedFile as any)?.dimensions?.[field.page];
      updateField(index, {
        ...field,
        id: field.id ? field.id : getNextAvailableId(selectedFile.fields),
        existingField: false,
        fieldPageX: pageX,
        fieldPageY: pageY,
        fieldPageHeight: pageHeight,
        fieldPageWidth: storedPageWidth,
        ...(dims?.x && dims?.y
          ? {
            x: (pageX / 100) * dims.x,
            y: (pageY / 100) * dims.y,
            w: (storedPageWidth / 100) * dims.x,
            h: (pageHeight / 100) * dims.y,
          }
          : {}),
      });
    },
    [
      getFieldPosition,
      selectedFile.fields,
      selectedFile.dimensions,
      isWithinPageBounds,
    ],
  );

  const onFieldMove = useCallback(
    (node: HTMLElement, index: number, event: any) => {
      const field = selectedFile.fields[index];

      const $page = window.document.querySelector<HTMLElement>(
        `${PDF_VIEWER_PAGE_SELECTOR}[data-page-number="${field.page + 1}"]`,
      );

      if (!$page) return;

      let { x: pageX, y: pageY } = getFieldPosition($page, node);
      if (pageX <= 0) pageX = 0.05;
      if (pageY <= 0) pageY = 0.1;

      // pageX/pageY are percentages (0–100) of the page. Reject only if the drop
      // is off the page. (The previous check divided by field.width/height —
      // the field's own pixel size, e.g. 108×21 — so dragging a field below
      // ~21% of the page height tripped it and the move was silently rejected,
      // making the field snap back.)
      if (pageX >= 100 || pageY >= 100) {
        return;
      }

      // Keep the PDF-point coords (x/y) in sync with the new percentage
      // position. Existing fields are positioned from x/y (calculateExitingCoords);
      // if we only updated the percentages the field would snap back to its stale
      // x/y on the next recompute.
      const dims = (selectedFile as any)?.dimensions?.[field.page];
      updateField(index, {
        ...field,
        id: field.id ? field.id : getNextAvailableId,
        existingField: false,
        fieldPageX: pageX,
        fieldPageY: pageY,
        ...(dims?.x && dims?.y
          ? { x: (pageX / 100) * dims.x, y: (pageY / 100) * dims.y }
          : {}),
      });
    },
    [
      getFieldPosition,
      selectedFile.fields,
      selectedFile.dimensions,
      isWithinPageBounds,
    ],
  );

  const openSelectedFieldDrawer = (e: any, field: any) => {
    const formattedField = {
      ...field,
      type: field.type?.replace(/_/g, " "),
    };

    if (!e.isDragging) {
      e.stopPropagation();
      setCurrentField(formattedField);
      if (sideCollpased) {
        dispatch(setSideCollpased(false));
      }
      dispatch(setSelectedFieldDrawer(true));
    } else {
      setCurrentField(formattedField);
    }
  };

  const getCommentsPopOver = () => {
    return (
      <Popover
        anchorEl={anchorEl}
        disableScrollLock
        onClose={popover.handleClose}
        open={popover.open}
        anchorOrigin={{
          horizontal: "center",
          vertical: "bottom",
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: 1.5,
            minWidth: 240,
            overflow: "hidden",
          },
        }}
      >
        <ContextMenu
          anchorEl={anchorEl}
          isOpen={Boolean(anchorEl)}
          setAnchorEl={popover.handleClose}
          field={currentField}
          isBulkSend={isBulkSend}
          isMobile={isMobile}
          onEditSignature={
            currentField?.id
              ? editSignatureCallbacks.current[currentField.id]
              : undefined
          }
        />
      </Popover>
    );
  };

  return (
    <Box
      id="pdf_box"
      ref={containerRef}
      // A scrolling region must be reachable by keyboard so it can be
      // scrolled without a pointer (WCAG 2.1.1).
      tabIndex={0}
      role="region"
      aria-label="Document pages"
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      height="100%"
      onScroll={handleScroll}
      onPointerDownCapture={(e) => {
        if (!isMobile) return;
        const target = e.target as HTMLElement;
        if (target.closest("[data-delete-field]")) return;
        const el = target.closest("[data-field-id]");
        if (!el) return;
        fieldPointerStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          id: Number(el.getAttribute("data-field-id")),
        };
      }}
      onPointerUpCapture={(e) => {
        if (!isMobile) return;
        const start = fieldPointerStartRef.current;
        fieldPointerStartRef.current = null;
        if (!start) return;
        const dx = Math.abs(e.clientX - start.x);
        const dy = Math.abs(e.clientY - start.y);
        // A real tap (not a drag): select the field and open its options bar.
        if (dx > 10 || dy > 10) return;
        const field = selectedFile.fields.find(
          (f: FileField) => f.id === start.id,
        );
        if (field) {
          dispatch(selectField(field));
          onFieldSelect?.(field);
        }
      }}
      position="relative"
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
        ...(!isMobile && {
          backgroundColor: editorTokens.canvas.bg,
          p: editorTokens.canvas.padding,
          overflowX: "auto",
          alignItems: "safe center",
          "& .react-pdf__Document": {
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 0,
          },
          "& .react-pdf__Document > *": {
            flexShrink: 0,
          },
        }),

        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",

        WebkitOverflowScrolling: "touch",
        touchAction: "pan-y pinch-zoom",

        "&::-webkit-scrollbar": {
          display: "block !important",
          width: isMobile ? "8px" : editorTokens.scrollbar.size,
          height: isMobile ? "8px" : editorTokens.scrollbar.size,
        },
        "&::-webkit-scrollbar-thumb": {
          border: editorTokens.scrollbar.thumbBorder,
          backgroundClip: "content-box",
          borderRadius: editorTokens.scrollbar.radius,
          backgroundColor: isMobile
            ? undefined
            : editorTokens.scrollbar.thumb,
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
      }}
    >
      <Box id={PLACED_FIELD_HELP_ID} sx={visuallyHidden}>
        Arrow keys move the field, Shift with an arrow moves it further, Alt
        with an arrow resizes it. Enter opens the field menu, Delete removes it.
      </Box>
      {topBanner &&
        (isMobile ? (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              px: 2,
              pb: 2,
            }}
          >
            {topBanner}
          </Box>
        ) : (
          // The design floats the AI pill over the top of the page rather than
          // reserving a row above it.
          <Box
            sx={{
              position: "absolute",
              top: editorTokens.aiBanner.top,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              display: "flex",
              maxWidth: "100%",
              px: 2,
            }}
          >
            {topBanner}
          </Box>
        ))}
      {!url || width === 0 ? (
        <Box
          role="status"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="85vh"
          width="100%"
        >
          <CircularProgress aria-hidden="true" />
          <Typography ml={2}>Loading document...</Typography>
        </Box>
      ) : (
        <Document
          key={url}
          file={url}
          onLoadSuccess={handleDocumentLoad}
          onLoadError={async () => {
            setDocLoading(true);
            // A render failure is almost always an expired S3 presigned URL.
            // Recover exactly like desktop: refresh the page dimensions (when
            // missing) and the document URL. We do both calls directly here
            // instead of relying on the redux → load-effect side chain, which
            // mobile timing can miss — so the recovery fires reliably on both.
            if (!isTruthy(selectedFile?.dimensions)) {
              getDocumentDimensions(
                selectedFile,
                isRequest,
                signRequest,
                dispatch,
              );
            }
            await getDocUrl();
          }}
          loading={() => (
            <Box
              role="status"
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="85vh"
            >
              <CircularProgress aria-hidden="true" />
              <Typography ml={2}>Loading document...</Typography>
            </Box>
          )}
        >
          {docLoading && (
            <Box
              role="status"
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ backgroundColor: "rgba(255,255,255,0.6)", zIndex: 1 }}
            >
              <CircularProgress aria-hidden="true" />
              <Typography ml={2}>Preparing document...</Typography>
            </Box>
          )}
          {visiblePages.map((pageNumber) => (
            <Box
              key={pageNumber}
              sx={{
                position: "relative",
                display: "inline-block",
                marginBottom: isMobile ? 2 : editorTokens.canvas.pageGap,
                ...(!isMobile && {
                  backgroundColor: "background.paper",
                  border: `1px solid ${editorTokens.canvas.pageBorder}`,
                  boxShadow: editorTokens.canvas.pageShadow,
                }),
              }}
              onClick={(e) => {
                if (isMobile) {
                  const target = e.target as HTMLElement;
                  const isFieldClick = target.closest(".react-draggable");
                  if (!isFieldClick) {
                    e.stopPropagation();
                    // Tapping empty document area deselects the active field so
                    // the mobile editor swaps the "Fields options" bar back to
                    // the field-type palette.
                    onFieldSelect?.(null);
                  }
                }
                const target = e.target as HTMLElement;
                if (target.closest(".react-draggable")) return;
                const pageEl = window.document.querySelector<HTMLElement>(
                  `${PDF_VIEWER_PAGE_SELECTOR}[data-page-number="${pageNumber}"]`,
                );
                if (!pageEl) return;
                const { fieldPageX, fieldPageY } = computePagePosition(
                  pageEl,
                  e.clientX,
                  e.clientY,
                );
                onPasteAnchor?.({
                  page: pageNumber - 1,
                  fieldPageX,
                  fieldPageY,
                });
              }}
              onContextMenu={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest(".react-draggable")) return;
                const pageEl = window.document.querySelector<HTMLElement>(
                  `${PDF_VIEWER_PAGE_SELECTOR}[data-page-number="${pageNumber}"]`,
                );
                if (!pageEl) return;
                e.preventDefault();
                const { fieldPageX, fieldPageY } = computePagePosition(
                  pageEl,
                  e.clientX,
                  e.clientY,
                );
                setPastePopover({
                  mouseX: e.clientX,
                  mouseY: e.clientY,
                  page: pageNumber - 1,
                  fieldPageX,
                  fieldPageY,
                });
              }}
              onTouchStart={(e) => {
                if (!isMobile) return;
                const target = e.target as HTMLElement;
                if (target.closest(".react-draggable")) return;
                if (e.touches.length !== 1) return;
                const touch = e.touches[0];
                longPressFiredRef.current = false;
                longPressStartRef.current = {
                  x: touch.clientX,
                  y: touch.clientY,
                };
                cancelLongPress();
                longPressTimerRef.current = setTimeout(() => {
                  longPressFiredRef.current = true;
                  longPressTimerRef.current = null;
                  const start = longPressStartRef.current;
                  if (!start) return;
                  const pageEl = window.document.querySelector<HTMLElement>(
                    `${PDF_VIEWER_PAGE_SELECTOR}[data-page-number="${pageNumber}"]`,
                  );
                  if (!pageEl) return;
                  const { fieldPageX, fieldPageY } = computePagePosition(
                    pageEl,
                    start.x,
                    start.y,
                  );
                  setPastePopover({
                    mouseX: start.x,
                    mouseY: start.y,
                    page: pageNumber - 1,
                    fieldPageX,
                    fieldPageY,
                  });
                  if (navigator.vibrate) navigator.vibrate(50);
                }, LONG_PRESS_DELAY);
              }}
              onTouchMove={(e) => {
                if (!isMobile) return;
                if (!longPressStartRef.current) return;
                const touch = e.touches[0];
                const dx = Math.abs(
                  touch.clientX - longPressStartRef.current.x,
                );
                const dy = Math.abs(
                  touch.clientY - longPressStartRef.current.y,
                );
                if (
                  dx > LONG_PRESS_MOVE_THRESHOLD ||
                  dy > LONG_PRESS_MOVE_THRESHOLD
                ) {
                  cancelLongPress();
                }
              }}
              onTouchEnd={() => {
                if (!isMobile) return;
                cancelLongPress();
              }}
              onTouchCancel={() => {
                if (!isMobile) return;
                cancelLongPress();
              }}
            >
              <Page
                pageNumber={pageNumber}
                width={renderedPageWidth}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                canvasRef={(node: HTMLCanvasElement | null) => {
                  if (node) {
                    node.setAttribute("role", "img");
                    node.setAttribute(
                      "aria-label",
                      `Document page ${pageNumber} of ${numPages}`,
                    );
                  }
                }}
                onLoadSuccess={(page: any) => handlePageLoad(pageNumber, page)}
              >
                {/* Desktop carries the page position in the toolbar caption
                    and the floating page pill, so the in-page footer would be
                    a third copy — mobile has neither, so it keeps it. */}
                {isMobile && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      backgroundColor: "transparent",
                      color: "text.primary",
                      borderRadius: "5px",
                      zIndex: 10,
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      align="center"
                      display="block"
                    >
                      Page {pageNumber} of {numPages}
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                  </Box>
                )}
              </Page>
              {selectedFile.fields
                .filter((field: FileField) => field.page + 1 === pageNumber)
                .map((field: FileField) => {
                  const index = selectedFile.fields.indexOf(field);
                  return (
                    <DraggableField
                      key={index}
                      field={field}
                      onFieldResize={(options, event: any) =>
                        onFieldResize(options, index, event)
                      }
                      onFieldMove={(options: any, event: any) =>
                        onFieldMove(options, index, event)
                      }
                      setAnchorEl={setAnchorEl}
                      onFieldClick={(e: any) => {
                        dispatch(selectField(field));
                        e.stopPropagation();
                        dispatch(setSideCollpased(false));
                        dispatch(setSelectedFieldDrawer(true));
                        setAnchorEl(null);
                        openSelectedFieldDrawer(e, field);
                        onFieldSelect?.(field);
                      }}
                      onFieldDoubleClick={(e: any, field: any) => {
                        dispatch(selectField(field));
                        e.stopPropagation();
                        dispatch(setSideCollpased(false));
                        dispatch(setSelectedFieldDrawer(true));
                        setAnchorEl(null);
                        openSelectedFieldDrawer(e, field);
                        onFieldSelect?.(field);
                      }}
                      popover={popover}
                      setCurrentField={setCurrentField}
                      isMobile={isMobile}
                      onRequestEditSignature={(
                        fieldId: number,
                        cb: () => void,
                      ) => {
                        editSignatureCallbacks.current[fieldId] = cb;
                      }}
                    />
                  );
                })}
            </Box>
          ))}
          {getCommentsPopOver()}
          <Popover
            open={pastePopover !== null}
            onClose={closePastePopover}
            anchorReference="anchorPosition"
            anchorPosition={
              pastePopover
                ? { top: pastePopover.mouseY, left: pastePopover.mouseX }
                : undefined
            }
            PaperProps={{
              elevation: 8,
              sx: {
                borderRadius: 1.5,
                minWidth: 220,
                overflow: "hidden",
              },
            }}
          >
            <MenuList
              {...tourAttr("request.pageContextMenu")}
              dense
              disablePadding
              sx={{ py: 0.5 }}
            >
              <MenuItem
                disabled={!canUndoDoc}
                onClick={() => {
                  if (canUndoDoc && docContextDocId) {
                    dispatch(undoFieldChange({ docId: docContextDocId }));
                  }
                  closePastePopover();
                }}
              >
                <ListItemIcon>
                  <UndoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Undo</ListItemText>
                <Typography
                  variant="caption"
                  sx={{ ml: 4, color: "text.secondary" }}
                >
                  Ctrl+Z
                </Typography>
              </MenuItem>
              <MenuItem
                disabled={!canRedoDoc}
                onClick={() => {
                  if (canRedoDoc && docContextDocId) {
                    dispatch(redoFieldChange({ docId: docContextDocId }));
                  }
                  closePastePopover();
                }}
              >
                <ListItemIcon>
                  <RedoIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Redo</ListItemText>
                <Typography
                  variant="caption"
                  sx={{ ml: 4, color: "text.secondary" }}
                >
                  Ctrl+Y
                </Typography>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                disabled={!canPaste}
                onClick={() => {
                  if (pastePopover && canPaste) {
                    pasteFieldAt?.(
                      pastePopover.page,
                      pastePopover.fieldPageX,
                      pastePopover.fieldPageY,
                    );
                  }
                  closePastePopover();
                }}
              >
                <ListItemIcon>
                  <ContentPasteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Paste</ListItemText>
                <Typography
                  variant="caption"
                  sx={{ ml: 4, color: "text.secondary" }}
                >
                  Ctrl+V
                </Typography>
              </MenuItem>
            </MenuList>
          </Popover>
          {selectedPreviewField && (
            <DragPreview
              type={selectedPreviewField}
              coords={previewCoords}
              isWithinBounds={isFieldWithinBounds}
              isMobile={isMobile}
            />
          )}
          {isLoadingPages && visiblePages.length < numPages && (
            <Box
              role="status"
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={2}
            >
              <CircularProgress size={24} aria-hidden="true" />
              <Typography ml={2}>Loading more pages...</Typography>
            </Box>
          )}
        </Document>
      )}
    </Box>
  );
};

export default DocumentViewer;
